import { UIIcon } from '../config/icons';
import { CHXAxis, CHXYAxes, CHYAxis } from '../lib/ch/axes';
import { CHChart } from '../lib/ch/chart';
import { CHColumnPlot } from '../lib/ch/column';
import { CHGridPlot } from '../lib/ch/grid';
import { CHLegendBar } from '../lib/ch/legend';
import { CHLinePlot } from '../lib/ch/line';
import { CHPiePlot } from '../lib/ch/pie';
import { CHPoint } from '../lib/ch/point';
import { CHColorMapper, CHLinearScale, CHNameScale, CHTimeScale } from '../lib/ch/scale';
import { CCityId, cCityBySlug, c_cities } from '../lib/mdl/c-city';
import { CEventTagId, cEventTagName, c_event_tags } from '../lib/mdl/c-event-tag';
import { p_promos } from '../lib/mdl/p-promo-admin';
import { STBundle, STCount, STFilter, STMetric, STOp, STParameter, STParameterSpec, STQuery, STSQLRow, STScale, STSeries, stQuery, stQueryProperty, stQuerySQL, st_counts, st_metrics, st_parameters } from '../lib/st/statistics';
import { $color, $colors } from '../lib/ui/color';
import { UIDataAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { uiInitCodeEditor } from '../lib/ui/component/codeeditor';
import { UIDateInput } from '../lib/ui/component/date';
import { UIDropdown, valueDropdownGenerator } from '../lib/ui/component/dropdown';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UISegmented } from '../lib/ui/component/segmented';
import { UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox, UIExclusiveToggles, UIMultiToggles, UIToggle } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { $el, UILink } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos, UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { makeCSV } from '../lib/y/csv';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $on, $trigger, YEventHandlers } from '../lib/y/events';
import { yTextToBlob } from '../lib/y/file-utils';
import { yClamp } from '../lib/y/math';
import { yEqual } from '../lib/y/object';
import { $date, $time } from '../lib/y/time';
import { $id, YId } from '../lib/y/types';

// class FilterDialog extends UIDialog {
//     constructor() {
//         super({ close: true, classes: 'm-500' });
//         const content = this.content;

//         const parameter_values: UIValue<STParameter>[] = (Object.keys(st_parameters) as STParameter[]).map(k =>
//             ({ key: k, name: st_parameters[k].name }));

//         const parameter = new UISelect(parameter_values, { kind: 'k-flat' });

//         content.ui_add($el.DIV('w-block l-form m-center', [
//             $el.H2('title', "New filter"),
//             parameter,
//             // sessions.map(s => $el.B(s.time_y.format('yyyy-MM-dd HH:mm'))),
//             // $el.DIV('s-small'),
//             // new UILabeled(amount, { above: 'Tickets per session' }),
//             // $el.DIV('l-button-row', [
//             //     new UIButton({ title: "Cancel", classes: 'm-round m-compact', on_click: () => { this.dismiss(null) } }),
//             //     new UIButton({ title: "Import", classes: 'm-round m-compact m-hollow', on_click: () => {
//             //         const value = amount.value || 0;
//             //         const new_sessions = sessions.map(s => ({ time: s.time_y, amount: value, imported: s.id }));
//             //         return cSessionsCreate(event.model.id, new_sessions).then(() => {
//             //             this.dismiss(true);
//             //         }, message => {
//             //             toast(message);
//             //         });
//             //     }})
//             // ])
//         ]));

//         this.ui_add(parameter);
//     }
// }

const tip_formats = { day: 'EEE d MMM', week: 'EEE d MMM', quarter: 'QQ yyyy', month: 'MMM yyyy', year: 'yyyy', time: 'yyyy', rel_year: '', rel_month: '', rel_week: '', total: '' }

const op_values: UIValue<STOp>[] = [
    { key: 'add', name: '+' },
    { key: 'sub', name: '–' },
    { key: 'mul', name: '×' },
    { key: 'div', name: '÷' }
];

class OperatorSelection extends UIExclusiveToggles<UIValue<STOp>, STOp> {
    constructor() {
        super(op_values, v => new UIToggle($el.SPAN(v.key == 'add' ? 'r-default r-segment' : 'r-segment', v.name), { key: v.key }), { classes: 'tl-operator-selection f-empty' });
    }
}

// class FilterSelection extends UIView {
//     constructor() {
//         super('div', 'tl-filter-selection');

//         const parameter_values: UIValue<STParameter>[] = (Object.keys(st_parameters) as STParameter[]).map(k =>
//             ({ key: k, name: st_parameters[k].name }));

//         const parameter = new UISelect(parameter_values, { kind: 'k-flat' });

//         this.ui_add(parameter);
//     }
// }

class MetricSelection extends UIView {
    $handlers: YEventHandlers<{ change: void, close: void }>;

    private _metric: UISelect<STMetric>;
    private _count: UISelect<STCount>;
    private _cumulative: UICheckbox;
    private _last_query?: STQuery;
    private _bundle?: Promise<STBundle>;

    constructor() {
        super('div', 'tl-metric-selection');

        const metric_values: UIValue<STMetric>[] = (Object.keys(st_metrics) as STMetric[]).filter(k => {
            const spec = st_metrics[k];
            if(k.startsWith('experiment_') && !xavis_experiments) return false;
            return !spec.when || spec.when();
        }).map(k => ({ key: k, name: st_metrics[k].name }));
        const count_values: UIValue<STCount>[] = (Object.keys(st_counts) as STCount[]).
            map(k => ({ key: k, name: st_counts[k] }));

        const metric = this._metric = new UISelect(metric_values, { kind: 'k-flat', placeholder: 'Metric' });
        const count = this._count = new UISelect(count_values, { kind: 'k-flat', placeholder: 'Count', disabled: true });
        const cumulative = this._cumulative = new UICheckbox({ title: 'Cumulative' });
        const add_filter = new UIButton({
            kind: 'k-text', title: '+ FILTER', on_click: () => {
                const m = metric.value;
                if(!m) return;
                const ms = st_metrics[m];
                const parameters = ms.parameters.map(p => ([p, st_parameters[p]] as [STParameter, STParameterSpec]));
                const popup = new UIDropdown<UIValue<STParameter>>(parameters.map(p => ({ key: p[0], name: p[1].name })), valueDropdownGenerator)
                // popup.on_choose = v => on_language(v.key);
                popup.open(add_filter, { position: UIRelPos.Below | UIRelPos.End, adjust_size: true, match_size: true, capture_mouse: true });
            }
        });
        // const filters_adapter = new UIDataAdapter((q: STQuery | undefined) => {
        //     // const selection = new PlotSelection(scale_input.value!);
        //     // // selection.value = q;
        //     // selection.on_series = fillStuff;
        //     // return selection;
        //     return new FilterSelection
        // })

        // const filters = new UIList(filters_adapter, { classes: 'r-filters' });

        this.ui_add([
            new UIButton({ icon: UIIcon.XMARK | UIIcon.WEIGHT_SOLID, kind: 'k-square', classes: 'r-close m-round', on_click: () => { $trigger(this, 'close') } }),
            metric,
            count,
            cumulative,
            xavis_experiments && add_filter
        ]);

        let dont_recalc = false;

        const recalc = () => {
            if(dont_recalc) return;
            if(metric.value)
                $trigger(this, 'change');
        };

        $on(metric, 'value', m => {
            dont_recalc = true;
            count.values = st_metrics[m!].counts.map(k => ({ key: k, name: st_counts[k] }));
            count.disabled = false;
            if(!count.value)
                count.value = 'all';
            dont_recalc = false;
            recalc();
        });

        $on(count, 'value', recalc);
        $on(cumulative, 'value', recalc);
    }

    get metric() {
        return this._metric.value;
    }

    getBundle(scale: STScale, group: STParameter[]) {
        const m = this._metric.value;
        if(!m) return undefined;

        const query: STQuery = {
            scale: scale,
            metric: this._metric.value!,
            counts: [this._count.value || 'all'],
            filter: {},
            group: group,
            to: $time.now().add(6, $time.month),
            pp: {
                accumulate: this._cumulative.value
            }
        };

        if(!this._last_query || !this._bundle || !yEqual(query, this._last_query)) {
            this._last_query = query;
            this._bundle = stQuery(query);
        }

        return this._bundle;
    }
}

class PlotSelection extends UIView {
    private _dont_recalc: boolean;

    private _scale: STScale;
    private _group_by: STParameter[];
    private _calc_token?: {};

    private _metrics: MetricSelection[];
    private _ops: OperatorSelection[];

    metric?: STMetric;
    bundle?: STBundle;
    tailed_bundle?: STBundle;
    on_series?: () => void;

    constructor(scale: STScale) {
        super('div', 'tl-plot-selection');

        this._scale = scale;
        this._group_by = [];
        this._dont_recalc = false;

        const metrics: MetricSelection[] = this._metrics = [];
        const ops: OperatorSelection[] = this._ops = [];

        const container = $el.DIV('r-metrics');

        const addMetric = () => {
            const metric = new MetricSelection;
            const op = new OperatorSelection;
            container.ui_add([metric, op]);
            metrics.push(metric);
            ops.push(op);
            $on(metric, 'change', () => {
                this._dont_recalc = true;
                filterGroupBy();
                this._dont_recalc = false;
                this._calcBundle()
            });

            $on(op, 'value', () => {
                if(op == ops[ops.length - 1]) {
                    addMetric();
                }
                filterGroupBy();
                this._calcBundle();
            });

            $on(metric, 'close', () => {
                if(metrics.length <= 1) return;
                let index = metrics.indexOf(metric);
                metric.remove();
                metrics.splice(index, 1);
                if(index) index--;
                ops[index].remove();
                ops.splice(index, 1);
                this._calcBundle();
            });
        };

        addMetric();

        this.ui_add(container);

        const params = (Object.keys(st_parameters) as STParameter[]).filter(p => {
            const spec = st_parameters[p];
            return !spec.when || spec.when();
        });

        const group_by = new UIMultiToggles(params, p => new UICheckbox({ key: p, title: st_parameters[p].name }), { classes: 'r-group' });

        const filterGroupBy = () => {
            const selected_metrics: STMetric[] = [];
            for(const m of metrics) {
                if(m.metric)
                    selected_metrics.push(m.metric);
            }
            group_by.values = params.filter(p => selected_metrics.length && selected_metrics.every(m => st_metrics[m].parameters.includes(p)));
        }

        filterGroupBy();

        this.ui_add(new UILabeled(group_by, { above: 'Group by' }));

        $on(group_by, 'value', v => {
            this._group_by = v;
            this._calcBundle();
        });
    }

    private _calcBundle() {
        if(this._dont_recalc) return;
        $log('app', 'calculating bundle');

        const bundles: { bundle: Promise<STBundle>; op: STOp; }[] = [];
        const metrics = this._metrics;
        const ops = this._ops;
        const scale = this._scale;
        const group_by = this._group_by;

        for(let i = 0; i < metrics.length; i++) {
            const b = metrics[i].getBundle(scale, group_by);
            if(b)
                bundles.push({ bundle: b, op: i > 0 ? ops[i - 1].value! : 'add' });
        }

        if(!bundles.length) return;

        const token = this._calc_token = {};

        Promise.all(bundles.map(b => b.bundle)).then(result => {
            if(token != this._calc_token) return;

            let final = result[0];

            for(let i = 1; i < result.length; i++) {
                final = final.op(bundles[i].op, result[i]);
            }

            this.bundle = final;
            this.tailed_bundle = final.tail(0);
            this.on_series && this.on_series();
        });
    }

    set scale(s: STScale) {
        this._scale = s;
        this._calcBundle();
    }

    markInvalid() {
        return false;
    }

    focus() {

    }
}

function calcLabel(prefix: string, group: STFilter) {
    const l: string[] = [];

    for(const g in group) {
        const param = st_parameters[g as STParameter];
        const label = param.label && param.label(group[g as STParameter]);
        if(label) l.push(label);
    }

    return prefix + (prefix && l.length ? ' - ' : '') + l.join('/');
}

export class AdminAnalyticsScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const fillStuff = () => {
            fillChart();
            fillTable();
        }

        const from_input = new UIDateInput;
        if(xavis_experiments)
            from_input.value = $date.make(2021, 11, 1);
        else
            from_input.value = $date.make(2017, 11, 1);
        const to_input = new UIDateInput;
        to_input.value = $date.today();

        const scale_values: UIValue<STScale>[] = [{ key: 'day', name: 'Day' }, { key: 'week', name: 'Week' }, { key: 'month', name: 'Month' },
        { key: 'quarter', name: 'Quarter' }, { key: 'year', name: 'Year' }, { key: 'time', name: 'Time' }];
        const scale_input = new UISegmented(scale_values, { classes: 'm-compact m-light-border r-scale' });
        scale_input.value = 'month';

        const download_button = new UIButton({ icon: UIIcon.DOWNLOAD, kind: 'k-square', classes: 'm-action m-round', link: '' });
        addTooltip('Download', download_button, { offset: 8, hover: download_button, position: UIRelPos.Above });
        download_button.ui_hide();
        content.ui_add(download_button);

        const selections_adapter = new UIDataAdapter((_q: STQuery | undefined) => {
            const selection = new PlotSelection(scale_input.value!);
            // selection.value = q;
            selection.on_series = fillStuff;
            return selection;
        })

        const selections = new UIList(selections_adapter, { classes: 'r-selections b' });

        const chart = new CHChart({ classes: 'b' });
        const table = new UITable<STSeries>();
        table.classList.add('b'); // TODO: add the option in table
        const legend = new CHLegendBar;
        legend.classList.add('b');

        content.ui_add($el.SECTION('w-block', [
            selections,
            $el.DIV('l-button-row b m-left', new UIButton({ kind: 'k-text', title: 'ADD NEW PLOT', on_click: () => { selections_adapter.push(undefined) } })),
            $el.DIV('tl-search-bar b', [$el.I('From'), from_input, $el.I('to'), to_input, $el.S(), scale_input]),
            $el.DIV('b', legend),
            chart,
            table
        ]));

        const fillChart = () => {
            const from = from_input.value;
            const to = to_input.value;
            const x_scale = new CHTimeScale;
            x_scale.tip_format = tip_formats[scale_input.value!];
            const axes = new CHXYAxes(new CHXAxis(x_scale), new CHYAxis(new CHLinearScale));

            let i = 0;
            const plots = selections_adapter.views;
            for(const p of plots) {
                const bundle = p.tailed_bundle;
                if(!bundle) continue;

                const prefix = '';
                // if(plots.length > 1)
                //     prefix = $tl.statistics_metrics[p.value!.metric].name;

                for(let j = 0; j < bundle.series.length; j++) {
                    const s = bundle.series[j];

                    axes.add(new CHLinePlot(bundle.linePoints(j, 0, from, to), {
                        color: s.key === 'other' ? $color.make(0xa0a0a0) : $color.make($colors.google[((i++) * 3 + 1) % $colors.google.length][5]),
                        label: s.key === 'other' ? 'Other' : calcLabel(prefix, s.group)
                    }));
                }
            }

            chart.setElements([axes]);
            legend.chart = chart
        };

        const fillTable = () => {
            download_button.ui_hide();
            const def = new UITableDefinition<STSeries>();

            const plots = selections_adapter.views;

            if(!plots.length) return;

            const bundle = plots[0].bundle;
            if(!bundle) return;

            const labels: { name: string, label: (v: any) => string, param: STParameter }[] = [];

            for(let b of bundle.group_by) {
                const param = st_parameters[b];
                const label = param.label;
                if(!label) continue;
                labels.push({ name: param.name, label, param: b });
                def.addValue(s => label(s.group[b]), { title: param.name, width: -160 });
            }

            let format: string;

            if(bundle.scale == 'month')
                format = 'yyyy-MM';
            else if(bundle.scale == 'year')
                format = 'yyyy';
            else
                format = 'yyyy-MM-dd';

            const dates = bundle.dates;
            const len = dates.length;
            let cols = 0;
            const now = $time.now();
            for(let i = 0; i < len && cols < 30; i++) {
                const date = $time(dates[len - i - 1]);
                if(date.after(now)) continue;
                def.addValue(s => s.values[0][len - i - 1], $fmt.NUMBERMAX(2), { title: date.format(format), width: -110 });
                cols++;
            }

            table.reset(def, new YArrayDataSource(bundle.series));

            const csv_array: (string | number)[][] = [];
            csv_array.push([...labels.map(l => l.name), ...dates.map(d => $time(d).format(format))]);
            for(let s of bundle.series) {
                csv_array.push([...labels.map(l => l.label(s.group[l.param])), ...s.values[0]]);
            }

            const csv = makeCSV(csv_array);
            const csv_url = URL.createObjectURL(yTextToBlob(csv, 'text/csv'));
            let csv_filename = 'statistics-' + bundle.metric;
            if(bundle.count != 'all')
                csv_filename += '-' + bundle.count;
            csv_filename += '-' + bundle.scale;
            if(bundle.group_by.length)
                csv_filename += '-' + bundle.group_by.join('-');
            csv_filename += '-' + $date.today().format('yyyyMMdd') + '.csv';

            download_button.link = { href: csv_url, download: csv_filename };

            download_button.ui_show();
        };

        $on(scale_input, 'value', s => {
            const plots = selections_adapter.views;
            for(const p of plots) {
                p.scale = s!;
            }
        });

        $on(from_input, 'value', fillStuff);
        $on(to_input, 'value', fillStuff);

        selections_adapter.data = [undefined];
    }
}

export class AdminMembersScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const city_values = [{ key: 'all', name: 'All cities' }];
        for(const c of c_cities.all) {
            if(c.can_signup)
                city_values.push({ key: c.id, name: c.name });
        }

        const city = new UISelect(city_values)
        city.value = 'all';

        const users = new CHChart({ classes: 'b' });
        const acquisition = new CHChart({ classes: 'b' });
        const acquisition_subtitle = $el.P('input-hint');
        const acquisition_legend = new CHLegendBar;
        acquisition_legend.classList.add('b', 'b-subtitle');
        const mrr = new CHChart({ classes: 'b' });
        const arpa = new CHChart({ classes: 'b' });
        const tags_subtitle = $el.P('input-hint');
        const tags = new CHChart({ classes: 'b' });
        const gender_subtitle = $el.P('input-hint');
        const gender = new CHChart({ classes: 'b' });
        const age_subtitle = $el.P('input-hint');
        const age = new CHChart({ classes: 'b' });

        const show_percentage = new UICheckbox({ title: 'Show percentage' });

        content.ui_add($el.SECTION('w-block', [
            $el.DIV('tl-search-bar b', city),
            $el.H3('heading', 'Active members and users'),
            users,
            $el.H3('heading', 'Acquisition and churn'),
            acquisition_subtitle,
            acquisition_legend,
            acquisition,
            $el.H3('heading', 'MRR and actual monthly revenue'),
            mrr,
            $el.H3('heading', 'Monthly revenue per active member'),
            arpa,
            $el.DIV('l-input-row', [
                $el.DIV('l-v i-horizontal', [$el.H3('heading', 'Picked event tags'), tags_subtitle, tags]),
                $el.DIV('l-v i-horizontal', [$el.H3('heading', 'Gender'), gender_subtitle, gender])
            ]),
            show_percentage,
            $el.H3('heading', 'Age distribution'),
            age_subtitle,
            age
        ]));

        const queries = [
            stQuery({ scale: 'day', metric: 'members_active', group: ['user_city'], counts: ['all', 'users', 'mrr'] }),
            stQuery({ scale: 'month', metric: 'action_subscribe', group: ['user_city'], counts: ['all'] }),
            stQuery({ scale: 'month', metric: 'cancellation', group: ['user_city'], counts: ['all'] }),
            stQuery({ scale: 'month', metric: 'action_resubscribe', group: ['user_city'], counts: ['all'] }),
            stQuery({ scale: 'month', metric: 'billing_payment', group: ['user_city'], counts: ['revenue'] }),
            stQuery({ scale: 'month', metric: 'members_active', group: ['user_city'], counts: ['all'] }),
            stQuery({ scale: 'total', metric: 'members_current', group: ['user_city', 'user_event_tags'], counts: ['all'] }),
            stQuery({ scale: 'total', metric: 'members_current', group: ['user_city', 'user_gender'], counts: ['all'] }),
            stQuery({ scale: 'total', metric: 'members_current', group: ['user_city', 'user_age_group'], counts: ['all'] }),
        ];

        Promise.all(queries).then(r => {
            const fillMost = () => {
                const v = city.value != 'all' ? $id.idToObjectId(city.value as YId) : undefined;

                const members_active_bundle = r[0].filter('user_city', v);
                const action_subscribe_bundle = r[1].filter('user_city', v);
                const cancellation_bundle = r[2].filter('user_city', v);
                const action_resubscribe_bundle = r[3].filter('user_city', v);
                const action_payment_bundle = r[4].filter('user_city', v);
                const members_active_month_bundle = r[5].filter('user_city', v);
                const members_age_bundle = r[8].filter('user_city', v);

                let axes = new CHXYAxes(new CHXAxis(new CHTimeScale), new CHYAxis(new CHLinearScale));
                axes.add(new CHLinePlot(members_active_bundle.linePoints(0, 0), { color: $color.make(0xe38d91) }));
                axes.add(new CHLinePlot(members_active_bundle.linePoints(0, 1), { color: $color.make(0x699294) }));
                users.setElements([axes]);

                axes = new CHXYAxes(new CHXAxis(new CHTimeScale), new CHYAxis(new CHLinearScale));
                axes.add(new CHLinePlot(action_subscribe_bundle.linePoints(0, 0), { color: $color.make(0x44bf3a), label: 'New subscriptions' }));
                axes.add(new CHLinePlot(cancellation_bundle.linePoints(0, 0), { color: $color.make(0xe3501e), label: 'Cancellations' }));
                axes.add(new CHLinePlot(action_resubscribe_bundle.linePoints(0, 0), { color: $color.make(0xf3c022), label: 'Resubscribes' }));
                acquisition.setElements([axes]);
                acquisition_legend.chart = acquisition
                const total_cancel = cancellation_bundle.sum(0, 0);
                const total_resubscribe = action_resubscribe_bundle.sum(0, 0);
                acquisition_subtitle.textContent = 'Reactivation rate: ' + (total_resubscribe / total_cancel * 100).toFixed(1) + '%.'// Average reactivation time: ' + 
                // (stats.resubscribe_time).toFixed(1) + ' days.'; 

                axes = new CHXYAxes(new CHXAxis(new CHTimeScale), new CHYAxis(new CHLinearScale));
                axes.add(new CHLinePlot(members_active_bundle.linePoints(0, 2), { color: $color.make(0x5f3d60) }));
                axes.add(new CHLinePlot(action_payment_bundle.linePoints(0, 0), { color: $color.make(0x5f3d60) }));
                mrr.setElements([axes]);

                const revenue_bundle = action_payment_bundle.op('div', members_active_month_bundle);
                axes = new CHXYAxes(new CHXAxis(new CHTimeScale), new CHYAxis(new CHLinearScale));
                axes.add(new CHLinePlot(revenue_bundle.linePoints(0, 0, $date.make(2016, 12, 1)), { color: $color.make(0x5f3d60) }));
                arpa.setElements([axes]);

                const age_data: CHPoint<string, number>[] = [];
                let age_total = 0;
                for(const g of members_age_bundle.series) {
                    const l = g.group['user_age_group'];
                    if(l) {
                        const y = g.values[0][0];
                        age_total += y;
                        age_data.push({ x: l, y: y });
                    }
                }
                age_data.sort((a, b) => a.x.localeCompare(b.x));
                age_subtitle.textContent = 'Based on ' + age_total + ' users';
                axes = new CHXYAxes(new CHXAxis(new CHNameScale(age_data.map(d => d.x))), new CHYAxis(new CHLinearScale));
                axes.add(new CHColumnPlot(age_data, { color: $color.make(0xe38d91) }));
                age.setElements([axes]);
            }

            const fillPies = () => {
                const v = city.value != 'all' ? city.value : undefined;
                const percentage = show_percentage.value;

                const members_tags_bundle = r[6].filter('user_city', v ? $id.idToObjectId(v as YId) : undefined);
                const members_gender_bundle = r[7].filter('user_city', v ? $id.idToObjectId(v as YId) : undefined);

                const gender_data: CHPoint<string, number>[] = [];
                let gender_total = 0;
                let current_total = 0;
                for(const g of members_gender_bundle.series) {
                    const y = g.values[0][0];
                    const l = g.group['user_gender'];
                    if(l) {
                        gender_total += y;
                        gender_data.push({ x: l, y: y });
                    }
                    current_total += y;
                }
                gender_subtitle.textContent = 'Based on ' + gender_total + ' users';
                gender.setElements([new CHPiePlot(gender_data, {
                    stroke: '#1f1f1f',
                    labels: true,
                    values: percentage ? (p => (p.y / gender_total * 100).toFixed(0) + '%') : true
                })]);

                const tag_data = members_tags_bundle.series.map(s => ({ x: cEventTagName(c_event_tags.maybe($id.objectIdToId(s.group['user_event_tags']) as CEventTagId)), y: s.values[0][0] }));
                tags_subtitle.textContent = 'Based on ' + current_total + ' users';
                tags.setElements([new CHPiePlot(tag_data, {
                    stroke: '#1f1f1f',
                    labels: true,
                    values: percentage ? (p => (p.y / current_total * 100).toFixed(0) + '%') : true
                })]);
            }

            $on(city, 'value', () => {
                fillMost();
                fillPies();
            });

            city.value = 'all';

            $on(show_percentage, 'value', fillPies);
        });
    }
}

function reshapeArray<T>(array: T[], rows: number) {
    const cols = array.length / rows;
    const ret: T[][] = [];
    for(let i = 0; i < rows; i++) {
        ret.push(array.slice(i * cols, i * cols + cols));
    }
    return ret;
}

export class AdminBookingsStatisticsScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const distribution_chart = new CHChart({ classes: 'b' });
        const used_distribution_chart = new CHChart({ classes: 'b' });
        const month_chart = new CHChart({ classes: 'b' });
        const hours_chart = new CHChart({ classes: 'b' });

        const now = $time.now();

        const queries = [
            stQuery({
                scale: 'total', metric: 'booking_used', counts: ['all'], group: ['user'], from: now.add(-3, $time.month),
                pp: { heavy: true }
            }),
            stQuery({
                scale: 'total', metric: 'booking_used', counts: ['all'], group: ['user'], from: now.add(-1, $time.month),
                pp: { heavy: true }
            }),
            stQuery({ scale: 'month', metric: 'booking_used', counts: ['all'], from: $date.make(2017, 12) }),
            stQuery({ scale: 'time', metric: 'booking_booked', counts: ['all'] }),
        ]

        Promise.all(queries).then(r => {
            {
                const data: CHPoint<number, number>[] = r[0].series[0].values[0].map((v, i) => ({ x: i + 1, y: v }));
                const axes = new CHXYAxes(new CHXAxis(new CHLinearScale), new CHYAxis(new CHLinearScale));
                axes.add(new CHLinePlot(data, { color: $color.make(0xe38d91) }));
                distribution_chart.addElement(axes);
            }

            {
                const data: CHPoint<number, number>[] = r[1].series[0].values[0].map((v, i) => ({ x: i + 1, y: v }));
                const axes = new CHXYAxes(new CHXAxis(new CHLinearScale), new CHYAxis(new CHLinearScale));
                axes.add(new CHLinePlot(data, { color: $color.make(0xe38d91) }));
                used_distribution_chart.addElement(axes);
            }

            {
                const data = r[2].columnPoints(0, 0);
                const axes = new CHXYAxes(new CHXAxis(new CHNameScale(data.map(m => m.x))), new CHYAxis(new CHLinearScale));
                axes.add(new CHColumnPlot(data, { color: $color.make(0xe38d91) }));
                month_chart.addElement(axes);
            }

            {
                const array = r[3].series[0].values[0];
                const axes = new CHXYAxes(new CHXAxis(new CHNameScale(['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11',
                    '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'])),
                    new CHYAxis(new CHNameScale(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])));
                axes.add(new CHGridPlot(reshapeArray(array, 7), { mapper: new CHColorMapper($color.make(0xffffff), $color.make(0x57385c)) }));
                hours_chart.addElement(axes);
            }
        });

        content.ui_add($el.SECTION('w-block', [
            $el.H3('heading', 'Booking distribution by users'),
            $el.H6('input-hint', 'Data from the last three months'),
            distribution_chart,
            $el.H3('heading', 'Used bookings distribution by users'),
            $el.H6('input-hint', 'Data from last month'),
            used_distribution_chart,
            $el.H3('heading', 'Bookings by month'),
            month_chart,
            $el.H3('heading', 'Booking times'),
            hours_chart
        ]));
    }
}

export class AdminSQLScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const editor_element = $el.DIV('tl-sql-editor b');

        const table = new UITable<STSQLRow>();

        let editor: AceAjax.Editor | undefined;

        uiInitCodeEditor().then(() => {
            editor = ace.edit(editor_element);
            editor.setShowFoldWidgets(false);
            editor.setShowPrintMargin(false);
            editor.renderer.setShowGutter(false);
            editor.session.setMode('ace/mode/sql');
            editor.focus();
        })

        content.ui_add($el.SECTION('w-block', [
            editor_element,
            $el.DIV('r-inputs', new UIButton({
                title: 'Run', on_click: () => {
                    if(!editor) return;
                    return stQuerySQL(editor.getValue()).then(result => {
                        $log('app', '%O', result);
                        const def = new UITableDefinition<STSQLRow>();
                        const cols = result.cols;
                        for(let i = 0; i < cols.length; i++) {
                            const c = cols[i];
                            if(c.type == 'UInt64' || c.type == 'UInt32')
                                def.addValue(v => v[i] as number, $fmt.NUMBER, { title: c.name });
                            else
                                def.addValue(v => v[i] + '', { title: c.name });
                        }

                        table.reset(def, new YArrayDataSource(result.rows));
                        table.ui_show();
                    }, err => {
                        $log('app', 'catching', err)
                        uiToast((err.data.data && err.data.data.body) ? err.data.data.body : err.data.message);
                        table.ui_hide();
                    });
                }
            })),
            table
        ]));
    }
}

export class AdminSegmentScreen extends UIScreen {
    protected override fillContent(_content: UIViewSpec): void {
        stQueryProperty({ property: { name: 'account_status' } });
        stQueryProperty({ property: { metric: 'booking_used', count: 'all' } });
        stQueryProperty({ property: { metric: 'booking_used', count: 'all', filters: [{ property: 'amount', op: 'eq', value: { int: 1 } }] } });
        stQueryProperty({
            property: {
                op: 'div',
                args: [{ metric: 'booking_used', count: 'all', filters: [{ property: 'amount', op: 'eq', value: { int: 1 } }] },
                { metric: 'booking_used', count: 'all', filters: [{ property: 'amount', op: 'eq', value: { int: 2 } }] }]
            }
        });
    }
}

type RetentionCount = 'all' | 'users' | 'not_canceling' | 'mrr' | 'mrr_user' | 'booked' | 'bookings' | 'ticket_value' | 'payout_value' | 'login';

interface RetentionCountSpec {
    name: string;
    query_metric: STMetric;
    query_count: STCount;
    div?: boolean;
    relative?: RetentionCount;
}

const retention_counts: { [key in RetentionCount]: RetentionCountSpec } = {
    all: {
        name: 'Members',
        query_metric: 'members_current',
        query_count: 'all'
    },
    users: {
        name: 'Users',
        query_metric: 'members_current',
        query_count: 'users'
    },
    not_canceling: {
        name: 'Not canceling',
        query_metric: 'members_not_canceling',
        query_count: 'all'
    },
    mrr: {
        name: 'MRR',
        query_metric: 'members_current',
        query_count: 'mrr'
    },
    mrr_user: {
        name: 'MRR per user',
        query_metric: 'members_current',
        query_count: 'mrr',
        div: true,
        relative: 'all'
    },
    booked: {
        name: 'Users booked',
        query_metric: 'booking_booked',
        query_count: 'unique_users',
        relative: 'all'
    },
    bookings: {
        name: 'Bookings used',
        query_metric: 'booking_used',
        query_count: 'all',
        div: true,
        relative: 'all'
    },
    ticket_value: {
        name: 'Ticket value',
        query_metric: 'booking_used',
        query_count: 'value',
        div: true,
        relative: 'all'
    },
    payout_value: {
        name: 'Payout value',
        query_metric: 'booking_used',
        query_count: 'payout',
        div: true,
        relative: 'all'
    },
    login: {
        name: 'Login',
        query_metric: 'login',
        query_count: 'unique_users',
        relative: 'all'
    },
}

type RetentionCohorts = { [key: string]: number[] };

function makeRetentionCell(value: number, base: number, absolute: boolean, div?: boolean, month_1_ref?: boolean) {
    const v = month_1_ref ? Math.max(value / base, 0) : yClamp(value / base, 0, 1);
    const formatted = absolute ? value + '' : div ? (value / base).toFixed(2) : (v * 100).toFixed(1) + '%';
    const td = $el.create('td', 'r-data', absolute ? value + '' : div ? (value / base).toFixed(2) : (v * 100).toFixed(1) + ' %');
    td.style.backgroundColor = 'hsl(298,28%,' + (100 - 60 * v).toFixed(1) + '%)';
    if(v > 0.5)
        td.style.color = 'white';
    else
        td.style.color = 'black';
    return [td, formatted] as const;
}

class RetentionChart extends UIView {
    constructor() {
        super('div', 'tl-retention-chart b');
    }

    setValue(count: RetentionCountSpec, cohorts: RetentionCohorts, ref_cohorts: RetentionCohorts | undefined, absolute: boolean, retention: STScale, rel_cohort: STParameter, month_1_ref: boolean, bundle: STBundle, filters: string) {
        this.ui_empty();

        const today = $date.today();
        // const date = $date.make(2017, 0, 1);
        const table = $el.create('table');
        const head = $el.create('thead');
        const body = $el.create('tbody');
        const all_base: number[] = [];
        const all: number[] = [];

        const csv_array: (string | number)[][] = [];

        for(let date = $date.make(2016, rel_cohort == 'year_cohort' ? 1 : 10, rel_cohort == 'week_cohort' ? 7 : 1); date.before(today); date = date.add(1, rel_cohort == 'year_cohort' ? $time.year : rel_cohort == 'week_cohort' ? $time.week : $time.month)) {
            const month = date.format(rel_cohort == 'year_cohort' ? 'yyyy' : rel_cohort == 'week_cohort' ? 'yyyy-MM-dd' : 'yyyy-MM');
            const n_months = Math.floor(today.sub(date.add(1, retention == 'rel_year' ? $time.year : retention == 'rel_week' ? $time.week : $time.month), retention == 'rel_year' ? $time.year : retention == 'rel_week' ? $time.week : $time.month) + 1);
            const cohort = cohorts[month];
            if(n_months <= 0 || !cohort || !cohort.length) continue;

            const tr = $el.create('tr');
            const formatted_month = date.format(rel_cohort == 'year_cohort' ? 'yyyy' : rel_cohort == 'week_cohort' ? 'yyyy-MM-dd' : 'MMM yyyy')
            tr.appendChild($el.create('td', 'r-month', formatted_month));

            const csv_row: (string | number)[] = [formatted_month];

            if(ref_cohorts) {
                const ref_cohort = ref_cohorts[month];
                const base = month_1_ref ? ref_cohort[1] : ref_cohort[0];
                if(!base) continue;
                tr.appendChild($el.create('td', 'r-count', base + ''));
                csv_row.push(base);

                for(let j = 0; j < n_months; j++) {
                    const rv = j < ref_cohort.length ? ref_cohort[j] : 0;
                    const v = j < cohort.length ? cohort[j] : 0;
                    const [td, formatted] = makeRetentionCell(v, rv || 1, absolute, count.div, month_1_ref);
                    tr.appendChild(td);
                    all[j] = (all[j] || 0) + v;
                    all_base[j] = (all_base[j] || 0) + rv;
                    csv_row.push(formatted);
                }
            } else {
                const base = month_1_ref ? cohort[1] : cohort[0];
                if(!base) continue;
                tr.appendChild($el.create('td', 'r-count', base + ''));
                csv_row.push(base);

                for(let j = 0; j < n_months; j++) {
                    const v = j < cohort.length ? cohort[j] : 0;
                    const [td, formatted] = makeRetentionCell(v, base, absolute, false, month_1_ref);
                    tr.appendChild(td);
                    all[j] = (all[j] || 0) + v;
                    all_base[j] = (all_base[j] || 0) + base;
                    csv_row.push(formatted);
                }
            }

            csv_array.push(csv_row);

            body.appendChild(tr);
        }

        const csv_total: (string | number)[] = ['Total', all[0].toFixed(0)];
        const csv_header = ['Cohort', count.name];

        const tr = $el.create('tr');
        tr.appendChild($el.create('td', 'r-month', 'All'));
        tr.appendChild($el.create('td', 'r-count', all[0].toFixed(0)));

        const trh = $el.create('tr');

        trh.appendChild($el.create('th', 'r-month', 'Cohort'));
        trh.appendChild($el.create('th', 'r-count', count.name));

        for(let i = 0; i < all.length; i++) {
            trh.appendChild($el.create('th', 'r-data', i + ''));
            csv_header.push(i + '');
            const [td, formatted] = makeRetentionCell(all[i], all_base[i] || 1, absolute, count.div);
            tr.appendChild(td)
            csv_total.push(formatted);
        }
        head.appendChild(trh);
        body.appendChild(tr);
        table.appendChild(head);
        table.appendChild(body);
        table.style.width = (200 + 75 * all.length) + 'px';
        this.ui_add(table);

        const csv = makeCSV([csv_header, ...csv_array, csv_total]);
        const csv_url = URL.createObjectURL(yTextToBlob(csv, 'text/csv'));
        let csv_filename = 'retention-' + bundle.metric;
        if(bundle.count != 'all')
            csv_filename += '-' + bundle.count;
        csv_filename += '-' + bundle.scale;
        csv_filename += filters;
        csv_filename += '-' + $date.today().format('yyyyMMdd') + '.csv';

        $log('app', 'filename', csv_filename);

        return { href: csv_url, download: csv_filename };
    }
}

export class AdminRetentionScreen extends UIScreen {
    protected override async fillContent(content: UIViewSpec) {
        const cohort_values: UIValue<STParameter>[] = [{ key: 'year_cohort', name: 'Yearly cohorts' }, { key: 'month_cohort', name: 'Monthly cohorts' }, { key: 'week_cohort', name: 'Weekly cohorts' }];
        const cohort = new UISelect(cohort_values);
        cohort.value = 'month_cohort';
        const retention_values: UIValue<STScale>[] = [{ key: 'rel_year', name: 'Yearly retention' }, { key: 'rel_month', name: 'Monthly retention' }, { key: 'rel_week', name: 'Weekly retention' }];
        const retention = new UISelect(retention_values);
        retention.value = 'rel_month';
        const one_ref = new UICheckbox({ title: 'Use 1 as reference' });

        const chart = new RetentionChart;

        const count = new UISelect((Object.keys(retention_counts) as RetentionCount[]).map(c => ({ key: c, name: retention_counts[c].name })));
        count.value = 'all';
        const city = new UISelect<CCityId | undefined>([{ key: undefined, name: 'All cities' }, { key: cCityBySlug('stockholm')!.id, name: 'Stockholm' },
        { key: cCityBySlug('goteborg')!.id, name: 'Göteborg' }, { key: cCityBySlug('berlin')!.id, name: 'Berlin' },
        { key: cCityBySlug('hamburg')!.id, name: 'Hamburg' }, { key: cCityBySlug('koeln')!.id, name: 'Köln' }]);
        city.value = undefined;
        const country = new UISelect([{ key: '', name: 'All countries' }, { key: 'SE', name: 'Sweden' }, { key: 'DE', name: 'Germany' }]);
        country.value = '';
        const kind = new UISelect([{ key: '', name: 'All kinds' }, { key: 'plusone', name: 'Plus 1' }, { key: 'standard', name: 'Standard' }]);
        kind.value = '';
        const absolute = new UICheckbox({ title: 'Show absolute values' });

        const all_promos = p_promos.all;

        const promo_values: UIValue<YId | undefined>[] = [{ key: undefined, name: 'All promos' }];
        for(const p of all_promos) {
            if(p.kind == 'signup' && !p.is_hidden)
                promo_values.push({ key: p.id, name: p.name });
        }
        const promo = new UISelect(promo_values);
        promo.value = undefined;

        const queries: { [k: string]: Promise<STBundle> } = {}

        const download_button = new UIButton({ icon: UIIcon.DOWNLOAD, kind: 'k-square', classes: 'm-action m-round', link: '' });
        addTooltip('Download', download_button, { offset: 8, hover: download_button, position: UIRelPos.Above });
        download_button.ui_hide();
        content.ui_add(download_button);

        function getQuery(count: RetentionCount) {
            const key = count + '|' + retention.value + '|' + cohort.value;

            if(!queries[key]) {
                const spec = retention_counts[count];
                queries[key] = stQuery({
                    scale: retention.value!, counts: [spec.query_count], metric: spec.query_metric,
                    group: ['subscription_kind', 'user_country', 'user_city', cohort.value!, 'promo']
                });
            }

            return queries[key];
        }

        function makeCohorts(bundle: STBundle) {
            const promo_value = promo.value ? $id.idToObjectId(promo.value) : undefined;
            $log('app', 'promo value is', promo.value);
            bundle = bundle.collapse({ user_city: city.value ? $id.idToObjectId(city.value) : undefined, user_country: country.value || undefined, subscription_kind: kind.value || undefined, promo: promo_value });

            const cohort_value = cohort.value!;
            const cohorts: RetentionCohorts = {};
            for(const s of bundle.series) {
                cohorts[s.group[cohort_value]] = s.values[0];
            }

            return cohorts;
        }

        async function recalc() {
            download_button.ui_hide();
            const c = count.value!;
            const spec = retention_counts[c];
            let download_link: UILink;

            let filters = '';
            if(country.value)
                filters += '-' + country.value;
            if(city.value)
                filters += '-' + c_cities.maybe(city.value)?.slug;
            if(kind.value)
                filters += '-' + kind.value;
            if(promo.value)
                filters += '-' + p_promos.maybe(promo.value)?.name;

            if(spec.relative) {
                const [abs, ref] = await Promise.all([getQuery(c), getQuery(spec.relative)]);
                const cohorts = makeCohorts(abs);
                const ref_cohorts = makeCohorts(ref);
                download_link = chart.setValue(spec, cohorts, ref_cohorts, absolute.value, retention.value!, cohort.value!, one_ref.value, abs, filters);
            } else {
                const abs = await getQuery(c)
                const cohorts = makeCohorts(abs);
                download_link = chart.setValue(spec, cohorts, undefined, absolute.value, retention.value!, cohort.value!, one_ref.value, abs, filters);
            }

            download_button.link = download_link;
            download_button.ui_show();
        }

        $on(city, 'value', recalc);
        $on(country, 'value', recalc);
        $on(kind, 'value', recalc);
        $on(count, 'value', recalc);
        $on(promo, 'value', recalc);
        $on(absolute, 'value', recalc);
        $on(cohort, 'value', recalc);
        $on(retention, 'value', recalc);
        $on(one_ref, 'value', recalc);

        content.ui_add($el.SECTION('w-block', [
            $el.DIV('tl-search-bar b', [cohort, retention, one_ref]),
            $el.DIV('tl-search-bar b', [count, country, city, kind, promo, absolute]),
            chart
        ]));

        recalc();
    }
}
